import { Button, useDisclosure, useToast } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useState, ReactNode } from 'react';
import useAuth from 'src/app/auth/use-auth';
import { ArticleType } from 'src/app/article/enum';
import {
  ArticleFormValues,
  CreateAs,
} from 'src/app/article/settings-modal/types';
import { getArticleDraftDateTitle } from 'src/app/article/utils';
import { useStudioMutation } from 'src/app/studio/hooks/use-studio-mutation';
import { useAutoSave, useEditor, Editor } from 'src/lib/editor';
import { EditorHeader } from 'src/lib/editor/editor-header';
import { logger } from 'src/lib/logger';
import { useArticleForm } from 'src/app/article/hooks/use-article-form';
import { Article } from 'src/app/article/types';
import { SvgVisibilityOutlined } from 'src/components/icons';
import SettingModal from '../../article/settings-modal';
import { EditorMode } from '../types';
import SuccessfullyModal from '../successfully-modal';

export interface StudioEditorProps {
  heading: ReactNode;
  studioId: string;
  editorMode: EditorMode;
  article?: Article;
}

export function StudioEditor({
  heading,
  studioId,
  article,
}: StudioEditorProps) {
  const [actionType, setActionType] = useState(CreateAs.DRAFT);
  const [isPublished, setIsPublished] = useState(false);
  const [editorDirty, setEditorDirty] = useState<boolean>(undefined);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const methods = useArticleForm(article);
  const router = useRouter();

  const autoSave = useAutoSave({
    enabled:
      actionType === CreateAs.DRAFT &&
      (!!editorDirty || methods.formState.isDirty),
    onSave: async () => {
      const values = methods.getValues();
      await onSubmit(values);
    },
  });

  const { isLoading, mutateAsync: createOrEditProject } = useStudioMutation();
  const toast = useToast();

  const { editor, titleRef } = useEditor({
    uniqueId: studioId,
    data: article?.content,
    areaType: 'STUDIO',
    imgType: 'studio_img',
    onChange: async (api) => {
      if (actionType === CreateAs.DRAFT) {
        const content = await api.saver.save();
        const hasContent = content.blocks.length > 0;
        if (hasContent) {
          setEditorDirty(true);
          autoSave.triggerTimer();
        }
      }
    },
  });
  const { user } = useAuth();

  const onSubmit = async (data: ArticleFormValues) => {
    if (isLoading) {
      return;
    }
    const articleTitle = titleRef.current?.getText() || article?.title;

    if (!articleTitle && actionType !== CreateAs.DRAFT) {
      toast({
        title: 'Failure 😢',
        description: 'Project title is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
      return;
    }

    const content = await editor.save();
    const {
      description,
      copyright,
      disabledComment,
      categories,
      tags: projectTags,
      contributors: projectContributors,
    } = data;

    const tags =
      projectTags?.length > 0 ? projectTags.map(({ value }) => value) : [];
    const contributors =
      projectContributors && projectContributors.length > 0
        ? projectContributors.map(({ value }) => value)
        : [];

    const values = {
      contentID: studioId,
      username: user.username,
      type: ArticleType.STUDIO,
      title:
        !articleTitle && actionType === CreateAs.DRAFT
          ? getArticleDraftDateTitle()
          : articleTitle,
      content,
      description,
      copyright,
      categories,
      tags,
      coCreators: contributors,
      commentable: !disabledComment,
      draft: actionType === CreateAs.DRAFT,
    };

    try {
      const response = await createOrEditProject(values);

      if (response.code === 1000) {
        if (isOpen) {
          onClose();
        }
      }

      if (actionType === CreateAs.PUBLISH) {
        setIsPublished(true);
      }
      setEditorDirty(false);
    } catch (err) {
      setIsPublished(false);
      logger.error(err);
    }
  };

  const handlePreviewNavigate = async () => {
    const values = methods.getValues();
    await onSubmit(values);
    router.push(`studio/${user.username}/${studioId}/preview`);
  };

  return (
    <Editor title={article?.title} titleRef={titleRef}>
      <>
        <EditorHeader
          heading={heading}
          saved={typeof editorDirty === 'boolean' && !editorDirty}
        >
          <>
            <Button
              isLoading={isLoading}
              onClick={handlePreviewNavigate}
              leftIcon={<SvgVisibilityOutlined fontSize="24px" />}
              rounded="ellipse"
              variant="ghost"
            >
              Preview
            </Button>
            <Button
              type="button"
              rounded="ellipse"
              onClick={() => {
                setActionType(CreateAs.PUBLISH);
                onOpen();
              }}
            >
              Publish
            </Button>
          </>
        </EditorHeader>
        <SettingModal
          loading={isLoading}
          methods={methods}
          onSubmit={methods.handleSubmit(onSubmit)}
          isOpen={isOpen}
          actionType={actionType}
          contentID={studioId}
          onClose={onClose}
        />

        <SuccessfullyModal
          actionType={actionType}
          projectId={studioId}
          visible={isPublished}
          setIsVisible={setIsPublished}
        />
      </>
    </Editor>
  );
}
